import React, { useState } from "react";
import { AnimatePresence, LayoutGroup, motion, useInView } from "framer-motion";
import { ArrowRight } from "lucide-react";
import ProductMockWindow from "@/components/ProductMockWindow";
import useAutoCycle from "@/hooks/useAutoCycle";
import { Button } from "@/components/ui/button";
import { accommodationCosts, baseCosts } from "@/lib/costCalculatorData";

const usdTable = baseCosts.usa;
const usaStayMid = accommodationCosts.usa.mid;

function usd(amount) {
  return `$${Number(amount).toLocaleString("en-US")}`;
}

const fade = {
  initial: { opacity: 0, y: 16 },
  animate: { opacity: 1, y: 0 },
  exit: { opacity: 0, y: -12 },
  transition: { duration: 0.45, ease: [0.22, 1, 0.36, 1] },
};

function StatusDot({ tone = "ok" }) {
  return <span className={`eh-dot eh-dot--${tone}`} aria-hidden="true" />;
}

function HospitalsMock() {
  const rows = [
    { name: "Memorial Sloan Kettering Cancer Center", meta: "New York City · matched to diagnosis" },
    { name: "MD Anderson Cancer Center", meta: "Houston · NCI comprehensive" },
    { name: "Mayo Clinic Cancer Center", meta: "Rochester, MN · verified profile" },
  ];
  return (
    <ProductMockWindow
      url="getbyonco.com/find-hospitals"
      footer={
        <>
          <span>NCI-designated US centers · city + cancer type</span>
          <span className="eh-pill eh-pill--ok">3 matches</span>
        </>
      }
    >
      <div className="eh-window-kicker">Hospital matches</div>
      <div className="eh-compare">
        <div className="eh-compare-col">
          <span>WITHOUT MATCHING</span>
          <div className="eh-mini-card eh-mini-card--old">
            <code>generic search</code>
            <p className="eh-strike">Best cancer hospital near me</p>
          </div>
          <div className="eh-mini-card eh-mini-card--old">
            <code>city listings</code>
            <p className="eh-strike">Every center, no filter by type</p>
          </div>
        </div>
        <div className="eh-compare-col">
          <span>BYONCO MATCHES</span>
          {rows.map((row, i) => (
            <motion.div
              key={row.name}
              className="eh-mini-card eh-mini-card--new"
              initial={{ opacity: 0, x: 10 }}
              animate={{ opacity: 1, x: 0 }}
              transition={{ delay: 0.12 * i, duration: 0.4 }}
            >
              <code>{row.name}</code>
              <p>{row.meta}</p>
            </motion.div>
          ))}
        </div>
      </div>
    </ProductMockWindow>
  );
}

function CostMock() {
  const rows = [
    { label: "Surgery", value: usd(usdTable.surgery) },
    { label: "Chemo, per cycle", value: usd(usdTable.chemo_per_cycle) },
    { label: "Radiation, per fraction", value: usd(usdTable.radiation_per_fraction) },
    { label: "PET-CT", value: usd(usdTable.pet_ct) },
  ];
  return (
    <ProductMockWindow
      url="getbyonco.com/cost-calculator"
      footer={<span>USA cost table · planning figures</span>}
    >
      <div className="eh-window-kicker">Cost estimate</div>
      <div className="eh-window-title">United States · breast cancer</div>
      <p className="eh-window-stat">{usd(usdTable.surgery)}</p>
      <p className="eh-window-hint">Surgery from our USA table. Chemo, radiation, and imaging listed below.</p>
      <div className="eh-progress" aria-hidden="true">
        <motion.span
          className="eh-progress-fill"
          initial={{ width: 0 }}
          animate={{ width: "68%" }}
          transition={{ duration: 1.1, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
      <div className="eh-row-list">
        {rows.map((row, i) => (
          <motion.div
            key={row.label}
            className="eh-row"
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.15 * i }}
          >
            <StatusDot />
            <span>{row.label}</span>
            <span className="eh-row-meta">{row.value}</span>
          </motion.div>
        ))}
      </div>
    </ProductMockWindow>
  );
}

function RareMock() {
  const rows = [
    { file: "DIPG / DMG", doc: "Dana-Farber Boston Children's, Boston", tone: "ok" },
    { file: "ATRT", doc: "St. Jude Children's Research Hospital, Memphis", tone: "ok" },
    { file: "NUT carcinoma", doc: "Dana-Farber Cancer Institute, Boston", tone: "ok" },
    { file: "Chordoma", doc: "UPMC Hillman Cancer Center, Pittsburgh", tone: "warn" },
  ];
  return (
    <ProductMockWindow
      url="getbyonco.com/rare-cancers"
      footer={
        <>
          <span>US NCI and pediatric pathways</span>
          <span className="eh-pill eh-pill--ok">Catalog live</span>
        </>
      }
    >
      <div className="eh-window-head">
        <div>
          <div className="eh-window-kicker">Specialist coverage</div>
          <div className="eh-window-title">Rare cancer pathways</div>
        </div>
        <div className="eh-window-metrics">
          <span>US centers</span>
          <span>39 types</span>
        </div>
      </div>
      <div className="eh-row-list">
        {rows.map((row, i) => (
          <motion.div
            key={row.file}
            className={`eh-row ${row.tone === "warn" ? "is-alert" : ""}`}
            initial={{ opacity: 0, y: 8 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * i }}
          >
            <StatusDot tone={row.tone} />
            <span className="eh-mono">{row.file}</span>
            <span className="eh-arrow">→</span>
            <span className="eh-row-meta">{row.doc}</span>
          </motion.div>
        ))}
      </div>
      <div className="eh-progress eh-progress--footer" aria-hidden="true">
        <motion.span
          className="eh-progress-fill"
          initial={{ width: 0 }}
          animate={{ width: "94%" }}
          transition={{ duration: 1.2, ease: [0.22, 1, 0.36, 1] }}
        />
      </div>
    </ProductMockWindow>
  );
}

function OpinionMock() {
  return (
    <ProductMockWindow
      url="getbyonco.com/app"
      activeNav="Care"
      footer={
        <>
          <span>2 reviews prepared · 0 remaining</span>
          <span className="eh-pill eh-pill--ok">Ready for review</span>
        </>
      }
    >
      <div className="eh-window-kicker">Second opinion</div>
      <div className="eh-window-title">Staging confirmation at an NCI center</div>
      <div className="eh-review-card">
        <div className="eh-review-head">
          <strong>Case review</strong>
          <span>5 reports · 2 opinions queued</span>
        </div>
        <motion.div className="eh-row" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.2 }}>
          <StatusDot tone="warn" />
          <span>Pathology report</span>
          <span className="eh-check">Complete</span>
        </motion.div>
        <motion.div className="eh-row" initial={{ opacity: 0, x: 8 }} animate={{ opacity: 1, x: 0 }} transition={{ delay: 0.4 }}>
          <StatusDot tone="info" />
          <span>Staging summary</span>
          <span className="eh-check">Prepared</span>
        </motion.div>
      </div>
    </ProductMockWindow>
  );
}

function TeleMock() {
  const doctors = [
    {
      name: "Dr. Monica Morrow",
      slot: "Breast surgery · Memorial Sloan Kettering, New York City",
      status: "On directory",
    },
    {
      name: "Dr. Amar Gajjar",
      slot: "Pediatric neuro-oncology · St. Jude, Memphis",
      status: "On directory",
    },
    {
      name: "Dr. Jia Luo",
      slot: "Thoracic oncology · Dana-Farber, Boston",
      status: "On directory",
    },
  ];
  return (
    <ProductMockWindow
      url="getbyonco.com/teleconsultation"
      footer={<span>Secure video · US specialists from our catalog</span>}
    >
      <div className="eh-window-kicker">Teleconsultation</div>
      <div className="eh-window-title">US oncologists</div>
      <div className="eh-row-list">
        {doctors.map((d, i) => (
          <motion.div
            key={d.name}
            className="eh-connect-row"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.12 * i }}
          >
            <span className="eh-connect-icon" />
            <div>
              <strong>{d.name}</strong>
              <span>{d.slot}</span>
            </div>
            <span className="eh-check">{d.status}</span>
          </motion.div>
        ))}
      </div>
    </ProductMockWindow>
  );
}

function TourismMock() {
  const links = [
    { title: "UnitedHealthcare", badge: "Insurance", meta: "commercial plan · from our USA insurer list" },
    { title: "MD Anderson Cancer Center", badge: "Hospital", meta: "Houston · NCI comprehensive" },
    { title: "Texas Medical Center lodging", badge: "Stay", meta: `${usd(usaStayMid)} / night mid rate · USA table` },
    { title: "English / Spanish coordinator", badge: "Care", meta: "languages listed for MD Anderson" },
  ];
  return (
    <ProductMockWindow
      url="getbyonco.com/journey-builder"
      footer={<span>Out-of-state US care · insurance and lodging</span>}
    >
      <div className="eh-window-kicker">Your journey</div>
      <div className="eh-window-title">Your care package</div>
      <div className="eh-row-list">
        {links.map((row, i) => (
          <motion.div
            key={row.title}
            className="eh-connect-row"
            initial={{ opacity: 0, y: 10 }}
            animate={{ opacity: 1, y: 0 }}
            transition={{ delay: 0.1 * i }}
          >
            <span className="eh-connect-icon" />
            <div>
              <strong>
                {row.title} <em className="eh-badge-inline">{row.badge}</em>
              </strong>
              <span>{row.meta}</span>
            </div>
            <span className="eh-check">Connected</span>
          </motion.div>
        ))}
      </div>
    </ProductMockWindow>
  );
}

const MOCKS = {
  hospitals: HospitalsMock,
  cost: CostMock,
  rare: RareMock,
  opinion: OpinionMock,
  tele: TeleMock,
  tourism: TourismMock,
};

export default function FeatureTabsShowcase({ items, onCta }) {
  const [paused, setPaused] = useState(false);
  const rootRef = React.useRef(null);
  const inView = useInView(rootRef, { amount: 0.25 });
  const { index, setIndex, progress } = useAutoCycle(items.length, 5600, paused || !inView);
  const active = items[index] || items[0];
  const Mock = MOCKS[active.id] || HospitalsMock;

  return (
    <div
      ref={rootRef}
      className="eh-showcase"
      onMouseEnter={() => setPaused(true)}
      onMouseLeave={() => setPaused(false)}
    >
      <LayoutGroup>
      <div className="eh-tablist" role="tablist" aria-label="ByOnco services">
        {items.map((item, i) => (
          <button
            key={item.id}
            type="button"
            role="tab"
            aria-selected={i === index}
            className={`eh-tab ${i === index ? "is-active" : ""}`}
            onClick={() => setIndex(i)}
          >
            {item.tab}
            {i === index && (
              <motion.span
                layoutId="eh-tab-underline"
                className="eh-tab-underline"
                transition={{ type: "spring", stiffness: 420, damping: 34 }}
              />
            )}
          </button>
        ))}
      </div>

      <div className="eh-showcase-grid">
        <div className="eh-showcase-copy">
          <AnimatePresence mode="wait">
            <motion.div key={active.id} {...fade}>
              <p className="eh-eyebrow">{active.tab}</p>
              <h3 className="eh-showcase-title">{active.title}</h3>
              <p className="eh-showcase-desc">{active.description}</p>
              {onCta && (
                <Button className="primary-cta eh-showcase-cta" onClick={() => onCta(active)}>
                  {active.cta || "Get Started"} <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              )}
            </motion.div>
          </AnimatePresence>
        </div>
        <div className="eh-showcase-visual">
          <AnimatePresence mode="wait">
            <motion.div
              key={active.id}
              initial={{ opacity: 0, y: 28, scale: 0.98 }}
              animate={{ opacity: 1, y: 0, scale: 1 }}
              exit={{ opacity: 0, y: -16, scale: 0.99 }}
              transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
            >
              <Mock />
            </motion.div>
          </AnimatePresence>
        </div>
      </div>

      <div className="eh-playhead" aria-hidden="true">
        <span className="eh-playhead-track">
          <motion.span
            className="eh-playhead-dot"
            animate={{ left: `${progress * 100}%` }}
            transition={{ duration: 0.08, ease: "linear" }}
          />
        </span>
      </div>
      </LayoutGroup>
    </div>
  );
}
